import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CitationAnchor } from '../types/api';
import { SearchedOpinion } from '../types';
import { ApiService } from '../services/apiService';
import { ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';

interface CitationSourcePanelProps {
  citations: CitationAnchor[];
  polarity?: string;
  onClose: () => void;
  targetStartOffset?: number;
  fullTextStartOffset?: number;
  fullTextEndOffset?: number;
}

/**
 * Generate portal link for an opinion
 */
const getOpinionPortalLink = (opinionId: string): string => {
  return `https://search.cnscaseportal.com/opinions/case/${opinionId}`;
};

/**
 * Expandable text component for long content
 */
const ExpandableText: React.FC<{ text: string; label: string; maxLines?: number }> = ({ text, label, maxLines = 3 }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text) return null;

  const lineHeight = 1.5;
  const maxHeight = `${maxLines * lineHeight}rem`;

  return (
    <div style={{ marginBottom: '15px' }}>
      <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
        {label}
      </div>
      <div style={{ position: 'relative' }}>
        <div
          style={{
            color: '#666',
            whiteSpace: 'pre-wrap',
            overflow: 'hidden',
            transition: 'all 0.3s',
            fontSize: '13px',
            lineHeight: '1.5rem',
            ...((!isExpanded && (text.split('\n').length > maxLines || text.length > 300)) ? { maxHeight } : {})
          }}
        >
          {text}
        </div>
        {(text.split('\n').length > maxLines || text.length > 300) && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            style={{
              marginTop: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: '#007bff',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500',
              padding: 0
            }}
          >
            {isExpanded ? (
              <>
                <ChevronUp style={{ width: '16px', height: '16px' }} />
                Show less
              </>
            ) : (
              <>
                <ChevronDown style={{ width: '16px', height: '16px' }} />
                Show more
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

// Color palette for numbered badges (sequential colors)
const BADGE_COLORS = [
  { bg: '#3b82f6', text: '#fff' }, // blue
  { bg: '#10b981', text: '#fff' }, // green
  { bg: '#f59e0b', text: '#fff' }, // orange
  { bg: '#8b5cf6', text: '#fff' }, // purple
  { bg: '#ec4899', text: '#fff' }, // pink
  { bg: '#06b6d4', text: '#fff' }, // cyan
  { bg: '#f97316', text: '#fff' }, // orange-red
  { bg: '#6366f1', text: '#fff' }, // indigo
];

interface LocatedCitation {
  startOffset: number;
  endOffset: number;
  citationIndex: number;
  isTarget: boolean;
  confidence: number;
}

/**
 * Side panel that displays the source document full text with highlighted citations.
 * Uses backend-computed full-text offsets for precise highlighting.
 * Falls back to chunk-based display if full text is unavailable.
 */
export const CitationSourcePanel: React.FC<CitationSourcePanelProps> = ({
  citations,
  polarity,
  onClose,
  targetStartOffset,
  fullTextStartOffset,
  fullTextEndOffset
}) => {
  const targetHighlightRef = useRef<HTMLSpanElement>(null);
  const [opinionDetails, setOpinionDetails] = useState<SearchedOpinion | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const firstCitation = citations?.[0] ?? null;

  // Fetch opinion details when panel opens
  useEffect(() => {
    if (!firstCitation) return;
    const fetchOpinionDetails = async () => {
      setIsLoadingDetails(true);
      try {
        const opinionId = parseInt(firstCitation.documentId, 10);
        if (!isNaN(opinionId)) {
          const apiService = ApiService.getInstance();
          const details = await apiService.getOpinionDetails([opinionId]);
          if (details && details.length > 0) {
            setOpinionDetails(details[0]);
          }
        }
      } catch (error) {
        console.error('Failed to fetch opinion details:', error);
      } finally {
        setIsLoadingDetails(false);
      }
    };

    fetchOpinionDetails();
  }, [firstCitation]);

  // Auto-scroll to target highlight after rendering
  useEffect(() => {
    if (targetHighlightRef.current) {
      setTimeout(() => {
        targetHighlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [targetStartOffset, opinionDetails]);

  // Sort citations by position in chunk text
  const sortedCitations = useMemo(() =>
    citations ? [...citations].sort((a, b) => a.startOffset - b.startOffset) : [],
    [citations]
  );

  // Use backend-provided offsets directly for highlighting, with client-side fallback
  const locatedCitations = useMemo((): LocatedCitation[] => {
    const fullText = opinionDetails?.fullText;
    if (!fullText) return [];

    // If full-text offsets are provided directly, use them
    if (fullTextStartOffset !== undefined && fullTextEndOffset !== undefined) {
      return [{
        startOffset: fullTextStartOffset,
        endOffset: fullTextEndOffset,
        citationIndex: 1,
        isTarget: true,
        confidence: sortedCitations[0]?.matchConfidence ?? 1
      }];
    }

    // Fallback: locate the citation's anchor text in the full text client-side
    const targetCitation = sortedCitations.find(c =>
      targetStartOffset !== undefined ? c.startOffset === targetStartOffset : true
    ) || sortedCitations[0];

    if (targetCitation) {
      const anchorText = targetCitation.anchorText;
      if (anchorText) {
        const idx = fullText.indexOf(anchorText);
        if (idx >= 0) {
          return [{
            startOffset: idx,
            endOffset: idx + anchorText.length,
            citationIndex: 1,
            isTarget: true,
            confidence: targetCitation.matchConfidence ?? 1
          }];
        }
      }

      // Last resort: use chunk text's cited span to search in full text
      const span = targetCitation.chunkText.substring(targetCitation.startOffset, targetCitation.endOffset);
      if (span.length > 20) {
        const idx = fullText.indexOf(span);
        if (idx >= 0) {
          return [{
            startOffset: idx,
            endOffset: idx + span.length,
            citationIndex: 1,
            isTarget: true,
            confidence: targetCitation.matchConfidence ?? 1
          }];
        }
      }
    }

    // No offsets available - return empty (will show chunk text fallback)
    return [];
  }, [opinionDetails?.fullText, sortedCitations, targetStartOffset, fullTextStartOffset, fullTextEndOffset]);

  if (!citations || citations.length === 0) return null;

  const hasFullTextMatches = locatedCitations.length > 0 && opinionDetails?.fullText;

  // Render full text with highlighted citations located via fuzzy matching
  const renderFullTextHighlighted = () => {
    const fullText = opinionDetails!.fullText!;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    for (const loc of locatedCitations) {
      // Add text before citation
      if (loc.startOffset > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`} style={{ color: '#444' }}>
            {fullText.substring(lastIndex, loc.startOffset)}
          </span>
        );
      }

      const colorIndex = (loc.citationIndex - 1) % BADGE_COLORS.length;
      const colors = BADGE_COLORS[colorIndex];
      const citedText = fullText.substring(loc.startOffset, loc.endOffset);

      parts.push(
        <span
          key={`citation-${loc.citationIndex}`}
          ref={loc.isTarget ? targetHighlightRef : null}
          style={{
            backgroundColor: loc.isTarget ? '#fde68a' : '#fff3cd',
            padding: '3px 6px',
            borderRadius: '4px',
            fontWeight: 'bold',
            position: 'relative',
            display: 'inline',
            boxShadow: loc.isTarget
              ? '0 0 0 3px rgba(245, 158, 11, 0.5)'
              : '0 0 0 2px rgba(255, 193, 7, 0.3)'
          }}
        >
          <span
            style={{
              display: 'inline-block',
              backgroundColor: colors.bg,
              color: colors.text,
              borderRadius: '50%',
              width: '18px',
              height: '18px',
              lineHeight: '18px',
              textAlign: 'center',
              fontSize: '10px',
              fontWeight: 'bold',
              marginRight: '4px',
              verticalAlign: 'baseline',
              position: 'relative',
              top: '-2px'
            }}
          >
            {loc.citationIndex}
          </span>
          {citedText}
        </span>
      );

      lastIndex = loc.endOffset;
    }

    // Add remaining text
    if (lastIndex < fullText.length) {
      parts.push(
        <span key={`text-${lastIndex}`} style={{ color: '#444' }}>
          {fullText.substring(lastIndex)}
        </span>
      );
    }

    return parts;
  };

  // Fallback: render chunk text with offset-based highlights (original behavior)
  const renderChunkTextHighlighted = () => {
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    const chunkText = firstCitation.chunkText;

    sortedCitations.forEach((citation, idx) => {
      const citationIndex = (citation as any).citationIndex || idx + 1;
      const colorIndex = (citationIndex - 1) % BADGE_COLORS.length;
      const colors = BADGE_COLORS[colorIndex];

      if (citation.startOffset > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`} style={{ color: '#666' }}>
            {chunkText.substring(lastIndex, citation.startOffset)}
          </span>
        );
      }

      const citedText = chunkText.substring(citation.startOffset, citation.endOffset);
      const isTarget = targetStartOffset !== undefined
        ? citation.startOffset === targetStartOffset
        : idx === 0;
      parts.push(
        <span
          key={`citation-${idx}`}
          ref={isTarget ? targetHighlightRef : null}
          style={{
            backgroundColor: isTarget ? '#fde68a' : '#fff3cd',
            padding: '3px 6px',
            borderRadius: '4px',
            fontWeight: 'bold',
            position: 'relative',
            display: 'inline',
            boxShadow: isTarget
              ? '0 0 0 3px rgba(245, 158, 11, 0.5)'
              : '0 0 0 2px rgba(255, 193, 7, 0.3)'
          }}
        >
          <span
            style={{
              display: 'inline-block',
              backgroundColor: colors.bg,
              color: colors.text,
              borderRadius: '50%',
              width: '18px',
              height: '18px',
              lineHeight: '18px',
              textAlign: 'center',
              fontSize: '10px',
              fontWeight: 'bold',
              marginRight: '4px',
              verticalAlign: 'baseline',
              position: 'relative',
              top: '-2px'
            }}
          >
            {citationIndex}
          </span>
          {citedText}
        </span>
      );

      lastIndex = citation.endOffset;
    });

    if (lastIndex < chunkText.length) {
      parts.push(
        <span key={`text-${lastIndex}`} style={{ color: '#666' }}>
          {chunkText.substring(lastIndex)}
        </span>
      );
    }

    return parts;
  };

  return (
    <div data-component="CitationSourcePanel" className="citation-source-panel" style={{
      position: 'fixed',
      right: 0,
      top: 0,
      width: 'min(1200px, 90vw)',
      height: '100vh',
      backgroundColor: '#fff',
      boxShadow: '-2px 0 8px rgba(0,0,0,0.1)',
      overflowY: 'auto',
      zIndex: 1000
    }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        padding: '20px',
        borderBottom: '1px solid #e5e7eb',
        position: 'sticky',
        top: 0,
        backgroundColor: '#fff',
        zIndex: 10
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Source Details</h3>
          <a
            href={getOpinionPortalLink(firstCitation.documentId)}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              color: '#007bff',
              textDecoration: 'none',
              padding: '4px'
            }}
            title="View in Case Portal"
          >
            <ExternalLink style={{ width: '18px', height: '18px' }} />
          </a>
        </div>
        <button 
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#666'
          }}
        >
          ×
        </button>
      </div>

      {/* Two-column layout */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr', 
        gap: '20px',
        padding: '20px',
        height: 'calc(100vh - 80px)',
        overflow: 'hidden'
      }}>
        {/* Left Column: Source Context */}
        <div style={{ 
          borderRight: '1px solid #e5e7eb', 
          paddingRight: '20px',
          overflowY: 'auto',
          height: '100%'
        }}>
          <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', fontWeight: '600', color: '#333' }}>
            {hasFullTextMatches ? 'Full Document Text' : 'Source Context (Passage)'}
          </h4>

          <div style={{ marginBottom: '15px' }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>
              Document ID: <code>{firstCitation.documentId}</code>
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
              {citations.length} citation{citations.length > 1 ? 's' : ''} from this source
              {hasFullTextMatches && (
                <span style={{ marginLeft: '8px', color: '#10b981', fontWeight: '500' }}>
                  • Located in full text
                </span>
              )}
              {!hasFullTextMatches && !isLoadingDetails && opinionDetails?.fullText && (
                <span style={{ marginLeft: '8px', color: '#f59e0b', fontWeight: '500' }}>
                  • Could not locate in full text, showing passage
                </span>
              )}
            </div>

            {/* Citation legend */}
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '10px', 
              borderRadius: '5px',
              marginBottom: '10px'
            }}>
              <div style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '5px', color: '#666' }}>
                {citations.length > 1 ? 'Citations in this source:' : 'Citation in this source:'}
              </div>
              {sortedCitations.map((citation, idx) => {
                const citationIndex = (citation as any).citationIndex || idx + 1;
                const colorIndex = (citationIndex - 1) % BADGE_COLORS.length;
                const colors = BADGE_COLORS[colorIndex];
                return (
                  <div key={idx} style={{ fontSize: '11px', marginBottom: '3px', display: 'flex', alignItems: 'center' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        backgroundColor: colors.bg,
                        color: colors.text,
                        borderRadius: '50%',
                        width: '16px',
                        height: '16px',
                        lineHeight: '16px',
                        textAlign: 'center',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        marginRight: '6px'
                      }}
                    >
                      {citationIndex}
                    </span>
                    <span style={{ color: '#666' }}>
                      Confidence: {Math.round(citation.matchConfidence * 100)}%
                    </span>
                    {polarity && (
                      <span style={{
                        display: 'inline-block',
                        padding: '1px 7px',
                        borderRadius: '8px',
                        fontSize: '10px',
                        fontWeight: '600',
                        marginLeft: '6px',
                        ...(polarity === 'contrary' ? { backgroundColor: '#fee2e2', color: '#b91c1c' } :
                           polarity === 'mixed' ? { backgroundColor: '#fef3c7', color: '#92400e' } :
                           polarity === 'supporting' ? { backgroundColor: '#d1fae5', color: '#065f46' } : {})
                      }}>
                        {polarity === 'contrary' ? 'Contrary' :
                         polarity === 'mixed' ? 'Mixed' :
                         polarity === 'supporting' ? 'Supporting' : ''}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {isLoadingDetails ? (
            <div style={{ color: '#666', fontSize: '14px', padding: '15px' }}>Loading full text...</div>
          ) : (
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '15px',
              borderRadius: '5px',
              fontSize: '14px',
              lineHeight: '1.8',
              fontFamily: 'Georgia, serif'
            }}>
              {hasFullTextMatches ? renderFullTextHighlighted() : renderChunkTextHighlighted()}
            </div>
          )}
        </div>

        {/* Right Column: Case Information */}
        <div style={{ 
          overflowY: 'auto',
          height: '100%'
        }}>
          <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', fontWeight: '600', color: '#333' }}>
            Case Information
          </h4>

          {isLoadingDetails ? (
            <div style={{ color: '#666', fontSize: '14px' }}>Loading case details...</div>
          ) : opinionDetails ? (
            <div>
              {opinionDetails.caseNumber && (
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
                    Case Number
                  </div>
                  <div style={{ fontSize: '13px', color: '#666' }}>{opinionDetails.caseNumber}</div>
                </div>
              )}

              {opinionDetails.fillingDate && (
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
                    Filing Date
                  </div>
                  <div style={{ fontSize: '13px', color: '#666' }}>{opinionDetails.fillingDate}</div>
                </div>
              )}

              {(opinionDetails.court || opinionDetails.courthouseName) && (
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
                    Court
                  </div>
                  <div style={{ fontSize: '13px', color: '#666' }}>
                    {opinionDetails.court}
                    {opinionDetails.courthouseName && ` - ${opinionDetails.courthouseName}`}
                  </div>
                </div>
              )}

              {opinionDetails.judge && (
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
                    Judge
                  </div>
                  <div style={{ fontSize: '13px', color: '#666' }}>{opinionDetails.judge}</div>
                </div>
              )}

              {(opinionDetails.plaintiffs?.length > 0 || opinionDetails.defendants?.length > 0) && (
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
                    Parties
                  </div>
                  {opinionDetails.plaintiffs?.length > 0 && (
                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '3px' }}>
                      <span style={{ fontWeight: '500' }}>Plaintiffs:</span> {opinionDetails.plaintiffs.join(', ')}
                    </div>
                  )}
                  {opinionDetails.defendants?.length > 0 && (
                    <div style={{ fontSize: '13px', color: '#666' }}>
                      <span style={{ fontWeight: '500' }}>Defendants:</span> {opinionDetails.defendants.join(', ')}
                    </div>
                  )}
                </div>
              )}

              {opinionDetails.categories?.length > 0 && (
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
                    Categories
                  </div>
                  <div style={{ fontSize: '13px', color: '#666' }}>{opinionDetails.categories.join(', ')}</div>
                </div>
              )}

              {opinionDetails.summary && (
                <ExpandableText text={opinionDetails.summary} label="Summary" maxLines={3} />
              )}
            </div>
          ) : (
            <div style={{ color: '#666', fontSize: '14px' }}>No case details available</div>
          )}
        </div>
      </div>
    </div>
  );
};


